import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const ONGLETS_DISPONIBLES = [
  { id: 'commandes',  label: 'Commandes' },
  { id: 'pilotes',    label: 'Pilotes' },
  { id: 'produits',   label: 'Produits' },
  { id: 'operateurs', label: 'Opérateurs' },
  { id: 'profils',    label: 'Profils' },
];

function FFMProfils() {
  const [profils, setProfils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProfil, setEditingProfil] = useState(null);
  const [formData, setFormData] = useState({ nom: '', onglets_accessibles: [] });
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    loadProfils();
  }, []);

  const loadProfils = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ffm_profils')
      .select('*, ffm_operateurs(id)')
      .order('date_creation', { ascending: false });

    if (error) {
      console.error('Erreur chargement profils FFM:', error);
    } else {
      setProfils(data || []);
    }
    setLoading(false);
  };

  const openModal = (profil = null) => {
    setErreur('');
    if (profil) {
      setEditingProfil(profil);
      setFormData({
        nom: profil.nom,
        onglets_accessibles: profil.onglets_accessibles || []
      });
    } else {
      setEditingProfil(null);
      setFormData({ nom: '', onglets_accessibles: [] });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProfil(null);
    setFormData({ nom: '', onglets_accessibles: [] });
    setErreur('');
  };

  const toggleOnglet = (id) => {
    setFormData(prev => ({
      ...prev,
      onglets_accessibles: prev.onglets_accessibles.includes(id)
        ? prev.onglets_accessibles.filter(o => o !== id)
        : [...prev.onglets_accessibles, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur('');

    if (!formData.nom.trim()) {
      setErreur('Le nom du profil est obligatoire.');
      return;
    }
    if (formData.onglets_accessibles.length === 0) {
      setErreur('Sélectionnez au moins un onglet accessible.');
      return;
    }

    const payload = {
      nom: formData.nom.trim(),
      onglets_accessibles: formData.onglets_accessibles
    };

    if (editingProfil) {
      const { error } = await supabase
        .from('ffm_profils')
        .update(payload)
        .eq('id', editingProfil.id);

      if (error) { setErreur('Erreur lors de la modification.'); return; }
    } else {
      const { error } = await supabase
        .from('ffm_profils')
        .insert([payload]);

      if (error) { setErreur('Erreur lors de la création.'); return; }
    }

    loadProfils();
    closeModal();
  };

  const handleDelete = async (profil) => {
    const nbOperateurs = profil.ffm_operateurs?.length || 0;
    const msg = nbOperateurs > 0
      ? `Ce profil est utilisé par ${nbOperateurs} opérateur(s). Les opérateurs associés n'auront plus de profil. Confirmer la suppression ?`
      : 'Supprimer ce profil ?';
    if (!window.confirm(msg)) return;

    const { error } = await supabase
      .from('ffm_profils')
      .delete()
      .eq('id', profil.id);

    if (error) { console.error('Erreur suppression:', error); }
    else { loadProfils(); }
  };

  // ─── Styles ──────────────────────────────────────────────────────────────────
  const styleBtnPrimary = {
    padding: '10px 20px', backgroundColor: '#003189', color: '#ffffff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'
  };
  const styleBtnSecondary = {
    padding: '8px 16px', backgroundColor: 'transparent', color: '#6c757d',
    border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
  };
  const styleBtnDanger = {
    padding: '8px 16px', backgroundColor: 'transparent', color: '#dc3545',
    border: '1px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
  };
  const styleInput = {
    width: '100%', padding: '10px 14px', border: '1px solid #dee2e6',
    borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  };
  const styleLabel = {
    display: 'block', fontSize: '13px', fontWeight: '600', color: '#495057', marginBottom: '6px'
  };
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#212529' }}>Profils FFM</h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6c757d' }}>
            {profils.length} profil{profils.length > 1 ? 's' : ''} — définissent les accès des opérateurs FFM
          </p>
        </div>
        <button style={styleBtnPrimary} onClick={() => openModal()}>+ Nouveau profil</button>
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: '60px' }}>Chargement...</div>
      ) : profils.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: '60px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🪪</div>
          <div style={{ fontSize: '16px', fontWeight: '600' }}>Aucun profil créé</div>
          <div style={{ fontSize: '13px', marginTop: '6px' }}>Créez un profil avant d'ajouter des opérateurs.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {profils.map(profil => (
            <div key={profil.id} style={{
              backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e9ecef',
              padding: '18px 22px', display: 'flex', alignItems: 'center', gap: '16px'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '700', color: '#212529' }}>{profil.nom}</span>
                  <span style={{
                    fontSize: '11px', padding: '2px 8px', borderRadius: '20px',
                    backgroundColor: '#e8f0fe', color: '#003189', fontWeight: '600'
                  }}>
                    {profil.ffm_operateurs?.length || 0} opérateur(s)
                  </span>
                </div>
                {/* Onglets accessibles */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {ONGLETS_DISPONIBLES.map(o => {
                    const actif = (profil.onglets_accessibles || []).includes(o.id);
                    return (
                      <span key={o.id} style={{
                        fontSize: '12px', padding: '3px 10px', borderRadius: '20px', fontWeight: '500',
                        backgroundColor: actif ? '#d4edda' : '#f8f9fa',
                        color: actif ? '#155724' : '#adb5bd',
                        border: `1px solid ${actif ? '#c3e6cb' : '#e9ecef'}`
                      }}>
                        {actif ? '✓' : '–'} {o.label}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={styleBtnSecondary} onClick={() => openModal(profil)}>Modifier</button>
                <button style={styleBtnDanger} onClick={() => handleDelete(profil)}>Supprimer</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── MODAL ──────────────────────────────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 24px', fontSize: '18px', fontWeight: '700', color: '#212529' }}>
              {editingProfil ? 'Modifier le profil' : 'Nouveau profil'}
            </h3>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '24px' }}>
                <label style={styleLabel}>Nom du profil *</label>
                <input
                  style={styleInput} type="text"
                  placeholder="Ex : Responsable communication, Graphiste..."
                  value={formData.nom}
                  onChange={e => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                  autoFocus
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={styleLabel}>Onglets accessibles *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {ONGLETS_DISPONIBLES.map(o => {
                    const checked = formData.onglets_accessibles.includes(o.id);
                    return (
                      <label key={o.id} style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                        border: `1px solid ${checked ? '#003189' : '#dee2e6'}`,
                        backgroundColor: checked ? '#e8f0fe' : '#ffffff',
                        transition: 'all 0.15s'
                      }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOnglet(o.id)}
                          style={{ width: '16px', height: '16px', accentColor: '#003189', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: checked ? '600' : '400', color: checked ? '#003189' : '#495057' }}>
                          {o.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {erreur && (
                <div style={{
                  padding: '10px 14px', backgroundColor: '#f8d7da',
                  color: '#721c24', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                }}>
                  {erreur}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" style={styleBtnSecondary} onClick={closeModal}>Annuler</button>
                <button type="submit" style={styleBtnPrimary}>
                  {editingProfil ? 'Enregistrer' : 'Créer le profil'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FFMProfils;
